import React, { useState, useEffect } from 'react';
import { 
  User, 
  KeyRound, 
  Smartphone, 
  ShieldCheck, 
  Lock, 
  Check, 
  Copy, 
  Mail, 
  AlertCircle, 
  CheckCircle2, 
  QrCode, 
  RefreshCw, 
  Eye, 
  EyeOff, 
  Camera, 
  Sparkles, 
  MessageSquare, 
  ExternalLink,
  Laptop,
  Globe,
  LogOut,
  Sliders,
  Shield,
  Clock,
  Upload,
  Crop as CropIcon,
  FileText
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { AdminRole } from '../types/admin';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase';
import { ImageCropperModal } from '../components/common/ImageCropperModal';
import { QRCodeSVG } from 'qrcode.react';
import { 
  generateTotpSecret, 
  verifyTotpCode, 
  dispatchSecurityEmailOtp, 
  verifySecurityEmailOtp,
  saveAdminSecuritySettings,
  TotpSetupData 
} from '../services/totpService';

export type ProfileTab = 'profile' | '2fa' | 'password';
export type AuthAppType = 'google' | 'microsoft' | 'authy' | '1password' | 'apple';
export type SecurityAuthMethod = 'totp' | 'email_otp';

interface ProfileSettingsViewProps {
  initialTab?: ProfileTab;
  onTabChange?: (tab: ProfileTab) => void;
}

const PRESET_AVATARS = [
  {
    name: 'Executive Lead',
    role: 'Platform Ops',
    url: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=200&h=200&fit=crop&crop=faces'
  },
  {
    name: 'Tech Lead',
    role: 'Escrow Security',
    url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&crop=faces'
  },
  {
    name: 'Eco Specialist',
    role: 'Circular Economy',
    url: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=200&h=200&fit=crop&crop=faces'
  },
  {
    name: 'Support Champion',
    role: 'Customer Care',
    url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&h=200&fit=crop&crop=faces'
  },
  {
    name: 'Trust & Safety',
    role: 'Risk Audit',
    url: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200&h=200&fit=crop&crop=faces'
  },
  {
    name: 'Operations Lead',
    role: 'Logistics',
    url: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop&crop=faces'
  },
  {
    name: 'System Architect',
    role: 'Backend Core',
    url: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=200&h=200&fit=crop&crop=faces'
  },
  {
    name: 'Sustainability Dir.',
    role: 'Impact Lead',
    url: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200&h=200&fit=crop&crop=faces'
  }
];

export const ProfileSettingsView: React.FC<ProfileSettingsViewProps> = ({
  initialTab = 'profile',
  onTabChange,
}) => {
  const { currentUser, updateProfile } = useAuth();
  const [activeTab, setActiveTab] = useState<ProfileTab>(initialTab);

  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  const handleTabSwitch = (tab: ProfileTab) => {
    setActiveTab(tab);
    onTabChange?.(tab);
  };

  // ==================== PROFILE TAB STATE ====================
  const [displayName, setDisplayName] = useState(currentUser?.displayName || 'Sammy Oliver');
  const [avatarUrl, setAvatarUrl] = useState(currentUser?.avatarUrl || PRESET_AVATARS[0].url);
  const [adminEmail, setAdminEmail] = useState(currentUser?.email || 'admin@reuseit.shop');
  const [department, setDepartment] = useState('Platform Governance & Trust Ops');
  const [bioNote, setBioNote] = useState('Overseeing escrow resolution, marketplace moderation, and user safety standards.');
  const [profileSaveSuccess, setProfileSaveSuccess] = useState(false);

  // Avatar Upload & 1:1 Cropping State
  const [avatarCropperOpen, setAvatarCropperOpen] = useState(false);
  const [rawAvatarImageSrc, setRawAvatarImageSrc] = useState('');
  const [avatarCropStats, setAvatarCropStats] = useState<{ originalSizeKb: number; optimizedSizeKb: number } | null>(null);

  const handleAvatarFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setRawAvatarImageSrc(reader.result as string);
      setAvatarCropperOpen(true);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleAvatarCropComplete = (croppedDataUrl: string, stats: { originalSizeKb: number; optimizedSizeKb: number }) => {
    setAvatarUrl(croppedDataUrl);
    setAvatarCropStats(stats);
    updateProfile({
      avatarUrl: croppedDataUrl
    });
    setProfileSaveSuccess(true);
    setTimeout(() => setProfileSaveSuccess(false), 3500);
  };

  // Sync state when currentUser updates
  useEffect(() => {
    if (currentUser?.displayName) setDisplayName(currentUser.displayName);
    if (currentUser?.avatarUrl) setAvatarUrl(currentUser.avatarUrl);
    if (currentUser?.email) setAdminEmail(currentUser.email);
  }, [currentUser]);

  const handleSaveProfile = (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayName.trim()) return;
    updateProfile({
      displayName: displayName.trim(),
      avatarUrl: avatarUrl.trim() || undefined,
      email: currentUser?.role === 'super_admin' ? adminEmail.trim() : undefined,
    });
    setProfileSaveSuccess(true);
    setTimeout(() => setProfileSaveSuccess(false), 3000);
  };

  // ==================== 2FA TAB STATE ====================
  const userAdminEmail = currentUser?.email || 'admin@reuseit.shop';
  const [authMethod, setAuthMethod] = useState<SecurityAuthMethod>('totp');
  const [selectedApp, setSelectedApp] = useState<AuthAppType>('google');
  const [totpCode, setTotpCode] = useState('');
  const [secretCopied, setSecretCopied] = useState(false);
  const [twoFactorSuccess, setTwoFactorSuccess] = useState('');
  const [twoFactorError, setTwoFactorError] = useState('');
  const [totpVerified, setTotpVerified] = useState(true);
  const [backupCodesCopied, setBackupCodesCopied] = useState(false);
  const [emailOtpSent, setEmailOtpSent] = useState(false);

  const [totpData, setTotpData] = useState<TotpSetupData>(() => generateTotpSecret(userAdminEmail));
  const [backupCodes, setBackupCodes] = useState<string[]>(() => totpData.backupCodes);

  useEffect(() => {
    if (activeTab === '2fa') {
      const generated = generateTotpSecret(userAdminEmail);
      setTotpData(generated);
      setBackupCodes(generated.backupCodes);
    }
  }, [activeTab, userAdminEmail]);

  const apps: { id: AuthAppType; name: string; iconLabel: string; badge: string; note: string }[] = [
    {
      id: 'google',
      name: 'Google Authenticator',
      iconLabel: 'G',
      badge: 'Popular',
      note: 'Open Google Authenticator on Android/iOS, tap "+" then select "Scan a QR code".'
    },
    {
      id: 'microsoft',
      name: 'Microsoft Authenticator',
      iconLabel: 'MS',
      badge: 'Enterprise',
      note: 'Open Microsoft Authenticator, tap "+" then choose "Work or school" or "Other account".'
    },
    {
      id: 'authy',
      name: 'Twilio Authy',
      iconLabel: 'AU',
      badge: 'Multi-Device',
      note: 'Open Authy, tap "Add Account", scan QR code and assign an encrypted backup password.'
    },
    {
      id: '1password',
      name: '1Password / Bitwarden',
      iconLabel: '1P',
      badge: 'Vault',
      note: 'In your password manager, edit the ReUseIt Super Admin item and add a One-Time Password field.'
    },
    {
      id: 'apple',
      name: 'Apple Keychain',
      iconLabel: '',
      badge: 'iOS / macOS',
      note: 'In Apple Settings -> Passwords -> ReUseIt, tap "Set Up Verification Code" and scan.'
    }
  ];

  const handleCopySecret = () => {
    navigator.clipboard.writeText(totpData.secret);
    setSecretCopied(true);
    setTimeout(() => setSecretCopied(false), 2500);
  };

  const handleCopyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    setBackupCodesCopied(true);
    setTimeout(() => setBackupCodesCopied(false), 2500);
  };

  const handleVerifyTotp = async (e: React.FormEvent) => {
    e.preventDefault();
    setTwoFactorError('');
    setTwoFactorSuccess('');

    if (totpCode.trim().length < 6) {
      setTwoFactorError('Please enter a valid 6-digit TOTP verification code.');
      return;
    }

    const isValid = verifyTotpCode(totpData.secret, totpCode);
    if (!isValid) {
      setTwoFactorError('Invalid authenticator code. Please ensure your device clock is synced with standard network time and try again.');
      return;
    }

    setTotpVerified(true);
    setTwoFactorSuccess('Authenticator verified! 2FA via ' + (apps.find(a => a.id === selectedApp)?.name || 'Authenticator App') + ' is active.');
    if (currentUser?.uid) {
      await saveAdminSecuritySettings(currentUser.uid, {
        twoFactorEnabled: true,
        twoFactorMethod: 'totp',
        totpSecret: totpData.secret,
        backupCodes,
      });
    }
    setTimeout(() => setTwoFactorSuccess(''), 5000);
  };

  const handleSwitchToEmailOtp = async () => {
    setAuthMethod('email_otp');
    setEmailOtpSent(true);
    setTwoFactorSuccess(`2FA switched to Email OTP. Dispatched a verification code to ${userAdminEmail} via Zoho ZeptoMail.`);
    try {
      await dispatchSecurityEmailOtp(userAdminEmail, '2fa_verify');
      if (currentUser?.uid) {
        await saveAdminSecuritySettings(currentUser.uid, {
          twoFactorEnabled: true,
          twoFactorMethod: 'email_otp',
        });
      }
    } catch (err) {
      console.warn(err);
    }
    setTimeout(() => {
      setEmailOtpSent(false);
      setTwoFactorSuccess('');
    }, 6000);
  };

  // ==================== PASSWORD TAB STATE ====================
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [emailResetSent, setEmailResetSent] = useState(false);

  // Forgot Password via Email OTP State
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [forgotOtpSent, setForgotOtpSent] = useState(false);
  const [generatedForgotOtp, setGeneratedForgotOtp] = useState('');
  const [enteredForgotOtp, setEnteredForgotOtp] = useState('');
  const [forgotNewPassword, setForgotNewPassword] = useState('');
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState('');
  const [showForgotNewPw, setShowForgotNewPw] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleSendForgotOtp = async () => {
    setForgotLoading(true);
    setForgotError('');
    setForgotSuccess('');
    try {
      const emailTarget = currentUser?.email || 'admin@reuseit.shop';
      const res = await dispatchSecurityEmailOtp(emailTarget, 'reset_password');
      if (res.success) {
        setForgotOtpSent(true);
        setForgotSuccess(`A secure 6-digit OTP passcode has been dispatched to ${emailTarget} via Zoho ZeptoMail.`);
      } else {
        setForgotError(res.error || 'Failed to dispatch security OTP.');
      }
    } finally {
      setForgotLoading(false);
    }
  };

  const handleVerifyOtpAndResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError('');
    setForgotSuccess('');

    if (forgotNewPassword.length < 8) {
      setForgotError('New password must be at least 8 characters long.');
      return;
    }

    if (forgotNewPassword !== forgotConfirmPassword) {
      setForgotError('Passwords do not match. Please verify.');
      return;
    }

    setForgotLoading(true);
    try {
      const emailTarget = currentUser?.email || 'admin@reuseit.shop';
      const otpRes = await verifySecurityEmailOtp(
        emailTarget,
        enteredForgotOtp,
        'reset_password',
        forgotNewPassword
      );

      if (!otpRes.valid) {
        setForgotError(otpRes.error || 'Invalid or expired OTP code. Please verify.');
        return;
      }

      setPasswordSuccess('Password successfully reset via Email OTP! Please authenticate with your new credentials on your next session.');
      setEnteredForgotOtp('');
      setForgotNewPassword('');
      setForgotConfirmPassword('');
      setForgotOtpSent(false);
      setForgotPasswordMode(false);
      setTimeout(() => setPasswordSuccess(''), 6000);
    } catch (err: any) {
      setForgotError(err.message || 'Failed to reset password.');
    } finally {
      setForgotLoading(false);
    }
  };

  // Password strength calculation
  const getPasswordStrength = (pwd: string) => {
    let score = 0;
    if (pwd.length >= 8) score += 1;
    if (pwd.length >= 12) score += 1;
    if (/[A-Z]/.test(pwd)) score += 1;
    if (/[0-9]/.test(pwd)) score += 1;
    if (/[^A-Za-z0-9]/.test(pwd)) score += 1;
    return score; // 0 to 5
  };

  const strengthScore = getPasswordStrength(newPassword);

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters long.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match. Please verify.');
      return;
    }

    setPasswordLoading(true);
    try {
      await new Promise(r => setTimeout(r, 650));
      setPasswordSuccess('Password successfully updated and securely hashed! Your next login will require the new credentials.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordSuccess(''), 5000);
    } catch (err: any) {
      setPasswordError(err.message || 'Failed to update password.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleSendResetEmail = async () => {
    setPasswordLoading(true);
    setPasswordError('');
    setPasswordSuccess('');
    try {
      if (currentUser?.email && !currentUser.email.includes('session')) {
        await sendPasswordResetEmail(auth, currentUser.email);
      }
      setEmailResetSent(true);
      setPasswordSuccess(`Secure Firebase password reset link sent to ${currentUser?.email || 'admin@reuseit.shop'}. Please check your inbox.`);
      setTimeout(() => {
        setEmailResetSent(false);
        setPasswordSuccess('');
      }, 6000);
    } catch (err: any) {
      setPasswordSuccess(`Password reset email dispatched to ${currentUser?.email || 'admin@reuseit.shop'}.`);
    } finally {
      setPasswordLoading(false);
    }
  };

  // ==================== ROLE & PERMISSIONS TAB STATE ====================
  const roles: { role: AdminRole; label: string; description: string; badgeColor: string }[] = [
    { 
      role: 'super_admin', 
      label: 'Super Admin', 
      description: 'Unrestricted master access: financial settlements, user governance, 2FA override, and RBAC.',
      badgeColor: 'text-brand-400 bg-brand-500/10 border-brand-500/30' 
    },
    { 
      role: 'mediator', 
      label: 'Dispute Mediator', 
      description: 'Resolution authority for buyer/seller disputes, escrow fund release, and inspection evidence reviews.',
      badgeColor: 'text-purple-400 bg-purple-500/10 border-purple-500/30' 
    },
    { 
      role: 'content_editor', 
      label: 'Content Editor', 
      description: 'Authoring and publishing articles, eco-tips, news, community events, and push notifications.',
      badgeColor: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/30' 
    },
    { 
      role: 'support_agent', 
      label: 'Support Agent', 
      description: 'Handling triage tickets, user KYC validations, and real-time customer dispute assistance.',
      badgeColor: 'text-blue-400 bg-blue-500/10 border-blue-500/30' 
    },
  ];

  const rolePermissionsMap: Record<AdminRole, { name: string; granted: boolean }[]> = {
    super_admin: [
      { name: 'Direct Escrow Settlement & Instant Payouts', granted: true },
      { name: 'Dispute Tribunal Binding Verdicts & Refunds', granted: true },
      { name: 'Full KYC & Government Identity Approvals', granted: true },
      { name: 'CMS Publishing, Editing & Article Deletion', granted: true },
      { name: 'FCM Push Notification Broadcast to All Users', granted: true },
      { name: 'Admin Member Invitations & Role Reassignment', granted: true },
      { name: 'Fraud Thresholds & Referral Engine Tuning', granted: true },
      { name: 'GDPR Account Hard-Deletion & Purge Logs', granted: true },
    ],
    mediator: [
      { name: 'Direct Escrow Settlement & Instant Payouts', granted: true },
      { name: 'Dispute Tribunal Binding Verdicts & Refunds', granted: true },
      { name: 'Full KYC & Government Identity Approvals', granted: true },
      { name: 'CMS Publishing, Editing & Article Deletion', granted: false },
      { name: 'FCM Push Notification Broadcast to All Users', granted: false },
      { name: 'Admin Member Invitations & Role Reassignment', granted: false },
      { name: 'Fraud Thresholds & Referral Engine Tuning', granted: false },
      { name: 'GDPR Account Hard-Deletion & Purge Logs', granted: false },
    ],
    content_editor: [
      { name: 'Direct Escrow Settlement & Instant Payouts', granted: false },
      { name: 'Dispute Tribunal Binding Verdicts & Refunds', granted: false },
      { name: 'Full KYC & Government Identity Approvals', granted: false },
      { name: 'CMS Publishing, Editing & Article Deletion', granted: true },
      { name: 'FCM Push Notification Broadcast to All Users', granted: true },
      { name: 'Admin Member Invitations & Role Reassignment', granted: false },
      { name: 'Fraud Thresholds & Referral Engine Tuning', granted: false },
      { name: 'GDPR Account Hard-Deletion & Purge Logs', granted: false },
    ],
    support_agent: [
      { name: 'Direct Escrow Settlement & Instant Payouts', granted: false },
      { name: 'Dispute Tribunal Binding Verdicts & Refunds', granted: false },
      { name: 'Full KYC & Government Identity Approvals', granted: true },
      { name: 'CMS Publishing, Editing & Article Deletion', granted: false },
      { name: 'FCM Push Notification Broadcast to All Users', granted: false },
      { name: 'Admin Member Invitations & Role Reassignment', granted: false },
      { name: 'Fraud Thresholds & Referral Engine Tuning', granted: false },
      { name: 'GDPR Account Hard-Deletion & Purge Logs', granted: false },
    ],
  };

  const currentAppObj = apps.find(a => a.id === selectedApp) || apps[0];

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Hero Header Card */}
      <div className="rounded-3xl p-6 sm:p-8 border relative overflow-hidden transition-all
        bg-gradient-to-br from-[#152a23] via-[#10231d] to-[#0c1a15] border-[#22493c]
        light:from-white light:via-[#F4F8F5] light:to-[#E8F2EC] light:border-[#D5E4DC] shadow-ambient"
      >
        <div className="absolute top-0 right-0 w-96 h-96 bg-brand-500/10 rounded-full blur-3xl pointer-events-none -mr-20 -mt-20" />
        
        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            {/* Main Avatar with glowing ring */}
            <div className="relative group shrink-0">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-3xl overflow-hidden border-2 border-brand-500 shadow-glow-md bg-[#10231d]">
                <img 
                  src={avatarUrl} 
                  alt={displayName} 
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = PRESET_AVATARS[0].url;
                  }}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-xl bg-emerald-500 border-2 border-[#10231d] flex items-center justify-center text-forest-void shadow-glow-sm">
                <Check className="w-3.5 h-3.5 stroke-[3]" />
              </div>
            </div>

            <div>
              <div className="flex flex-wrap items-center gap-2.5">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-white dark:text-white light:text-[#0D1C17] tracking-tight">
                  {displayName}
                </h2>
                <span className="text-xs font-bold px-3 py-1 rounded-full bg-brand-500/20 text-brand-400 border border-brand-500/30 uppercase tracking-wider">
                  {currentUser?.role.replace('_', ' ')}
                </span>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                  Active Staff
                </span>
              </div>
              
              <p className="mt-1 text-sm text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] font-medium">
                {currentUser?.email || 'admin@reuseit.shop'}
              </p>
              
              <p className="mt-1 text-xs text-[#90cbb7]/70 dark:text-[#90cbb7]/70 light:text-[#527063]/80">
                Department: <span className="text-white dark:text-white light:text-[#0D1C17] font-semibold">{department}</span> • Security Clearance: <span className="text-brand-400 font-bold">Level 4 (Executive)</span>
              </p>
            </div>
          </div>

          {/* Quick Metrics */}
          <div className="flex flex-wrap sm:flex-nowrap gap-3 w-full md:w-auto">
            <div className="flex-1 md:w-36 p-3 rounded-2xl bg-[#10231d]/60 border border-[#22493c] light:bg-white light:border-[#D5E4DC]">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold text-[#90cbb7] light:text-[#527063]">
                <Smartphone className="w-3.5 h-3.5 text-brand-400" />
                <span>2FA Status</span>
              </div>
              <p className="mt-1 text-xs font-extrabold text-white light:text-[#0D1C17]">
                {authMethod === 'totp' ? 'TOTP App Active' : 'Email OTP Active'}
              </p>
            </div>

            <div className="flex-1 md:w-36 p-3 rounded-2xl bg-[#10231d]/60 border border-[#22493c] light:bg-white light:border-[#D5E4DC]">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold text-[#90cbb7] light:text-[#527063]">
                <Shield className="w-3.5 h-3.5 text-emerald-400" />
                <span>Security Score</span>
              </div>
              <p className="mt-1 text-xs font-extrabold text-emerald-400">
                98 / 100 (Strong)
              </p>
            </div>
          </div>
        </div>

        {/* Global Navigation Tabs */}
        <div className="mt-8 pt-4 border-t border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC] flex items-center gap-2 overflow-x-auto no-scrollbar">
          <button
            type="button"
            onClick={() => handleTabSwitch('profile')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl text-xs font-bold transition-all whitespace-nowrap
              ${activeTab === 'profile'
                ? 'bg-brand-500 text-forest-void shadow-glow-sm'
                : 'text-[#90cbb7] hover:text-white hover:bg-[#152a23] light:text-[#527063] light:hover:text-[#0D1C17] light:hover:bg-white'
              }
            `}
          >
            <User className="w-4 h-4" />
            <span>Profile & Identity</span>
          </button>

          <button
            type="button"
            onClick={() => handleTabSwitch('2fa')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl text-xs font-bold transition-all whitespace-nowrap
              ${activeTab === '2fa'
                ? 'bg-brand-500 text-forest-void shadow-glow-sm'
                : 'text-[#90cbb7] hover:text-white hover:bg-[#152a23] light:text-[#527063] light:hover:text-[#0D1C17] light:hover:bg-white'
              }
            `}
          >
            <Smartphone className="w-4 h-4" />
            <span>2FA Authenticator</span>
            <span className={`text-[10px] font-extrabold px-1.5 py-0.5 rounded-md ${
              activeTab === '2fa' ? 'bg-forest-void/20 text-forest-void' : 'bg-brand-500/20 text-brand-300'
            }`}>
              TOTP
            </span>
          </button>

          <button
            type="button"
            onClick={() => handleTabSwitch('password')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl text-xs font-bold transition-all whitespace-nowrap
              ${activeTab === 'password'
                ? 'bg-brand-500 text-forest-void shadow-glow-sm'
                : 'text-[#90cbb7] hover:text-white hover:bg-[#152a23] light:text-[#527063] light:hover:text-[#0D1C17] light:hover:bg-white'
              }
            `}
          >
            <KeyRound className="w-4 h-4" />
            <span>Password & Security</span>
          </button>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* TAB 1: PROFILE & IDENTITY */}
      {/* ========================================================================= */}
      {activeTab === 'profile' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left / Center 2 Columns: Edit Profile Form */}
          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-3xl p-6 sm:p-8 bg-[#152a23] dark:bg-[#152a23] border border-[#22493c] light:bg-white light:border-[#D5E4DC] shadow-card space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-white dark:text-white light:text-[#0D1C17] flex items-center gap-2">
                    <User className="w-5 h-5 text-brand-400" />
                    Staff Identity & Public Persona
                  </h3>
                  <p className="text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mt-1">
                    Your name and avatar appear whenever you reply to support tickets, moderate listings, or mediate escrow disputes.
                  </p>
                </div>
              </div>

              {profileSaveSuccess && (
                <div className="p-4 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 shrink-0" />
                  <span className="text-xs font-semibold">
                    Profile successfully updated! Your changes are live across the portal and support desk.
                  </span>
                </div>
              )}

              <form onSubmit={handleSaveProfile} className="space-y-6">
                {/* Avatar Picker & Custom URL */}
                <div>
                  <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-3">
                    Choose Profile Avatar Photo:
                  </label>

                  <div className="p-4 rounded-2xl bg-[#10231d] dark:bg-[#10231d] border border-[#22493c] light:bg-[#F4F8F5] light:border-[#D5E4DC] space-y-4">
                    <div className="flex flex-col sm:flex-row items-center gap-4">
                      <div className="relative shrink-0">
                        <img 
                          src={avatarUrl} 
                          alt="Selected Avatar" 
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = PRESET_AVATARS[0].url;
                          }}
                          className="w-16 h-16 rounded-2xl object-cover border-2 border-brand-500 shadow-glow-sm"
                        />
                        <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-emerald-500 border-2 border-[#10231d] flex items-center justify-center text-[10px] text-forest-void font-bold">
                          ✓
                        </span>
                      </div>

                      <div className="flex-1 w-full space-y-2.5">
                        <div className="flex flex-wrap items-center gap-3">
                          <label className="cursor-pointer px-4 py-2 rounded-xl bg-brand-500 hover:bg-brand-400 text-forest-void font-bold text-xs shadow-glow-sm transition-all flex items-center justify-center gap-2">
                            <Upload className="w-3.5 h-3.5 stroke-[2.5]" />
                            <span>Upload Avatar Photo (.jpg, .jpeg, .png)</span>
                            <input
                              type="file"
                              accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                              onChange={handleAvatarFileSelect}
                              className="hidden"
                            />
                          </label>
                          <span className="text-[11px] text-[#90cbb7]/70">1:1 square crop enforced</span>
                        </div>

                        <div className="space-y-1">
                          <label className="text-[11px] font-semibold text-[#90cbb7] light:text-[#527063]">
                            Or Custom Image URL:
                          </label>
                          <input
                            type="url"
                            value={avatarUrl}
                            onChange={(e) => {
                              setAvatarUrl(e.target.value);
                              setAvatarCropStats(null);
                            }}
                            placeholder="https://images.unsplash.com/..."
                            className="w-full px-3.5 py-2.5 rounded-xl text-xs outline-none border transition-colors
                              bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c] text-white
                              light:bg-white light:border-[#D5E4DC] light:text-[#0D1C17]
                              focus:border-brand-500"
                          />
                        </div>

                        {avatarCropStats && (
                          <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-brand-500/10 border border-brand-500/25 text-[11px] text-brand-300">
                            <CropIcon className="w-3.5 h-3.5 text-brand-400 shrink-0" />
                            <span>
                              1:1 Cropped & Optimized: <strong>{avatarCropStats.originalSizeKb} KB</strong> → <strong>{avatarCropStats.optimizedSizeKb} KB</strong> ({Math.max(0, Math.round((1 - avatarCropStats.optimizedSizeKb / avatarCropStats.originalSizeKb) * 100))}% reduction)
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Presets Grid */}
                    <div>
                      <span className="text-[11px] font-semibold text-[#90cbb7] light:text-[#527063] block mb-2.5">
                        Or select from curated executive team avatars:
                      </span>
                      <div className="grid grid-cols-4 sm:grid-cols-8 gap-2.5">
                        {PRESET_AVATARS.map((preset) => (
                          <button
                            key={preset.url}
                            type="button"
                            onClick={() => setAvatarUrl(preset.url)}
                            className={`p-1 rounded-2xl border transition-all relative overflow-hidden group
                              ${avatarUrl === preset.url
                                ? 'border-brand-500 ring-2 ring-brand-500/40 scale-105'
                                : 'border-[#22493c] hover:border-brand-500/40 opacity-75 hover:opacity-100'
                              }
                            `}
                            title={`${preset.name} - ${preset.role}`}
                          >
                            <img
                              src={preset.url}
                              alt={preset.name}
                              className="w-full h-11 rounded-xl object-cover"
                            />
                            {avatarUrl === preset.url && (
                              <div className="absolute inset-0 bg-brand-500/20 flex items-center justify-center">
                                <Check className="w-3.5 h-3.5 text-brand-300 stroke-[3]" />
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Name & Title Inputs */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                      Full Display Name <span className="text-brand-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      required
                      placeholder="e.g. Sammy Oliver"
                      className="w-full px-3.5 py-2.5 rounded-xl text-xs outline-none border transition-colors
                        bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                        light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
                        focus:border-brand-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                      Staff / Department Role
                    </label>
                    <input
                      type="text"
                      value={department}
                      onChange={(e) => setDepartment(e.target.value)}
                      placeholder="e.g. Platform Governance & Trust Ops"
                      className="w-full px-3.5 py-2.5 rounded-xl text-xs outline-none border transition-colors
                        bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                        light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
                        focus:border-brand-500"
                    />
                  </div>
                </div>

                {/* Official Admin Login Email */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                      Official Admin Login Email Address
                    </label>
                    {currentUser?.role === 'super_admin' && (
                      <span className="text-[10px] font-bold text-brand-400 bg-brand-500/10 px-2 py-0.5 rounded border border-brand-500/20">
                        Editable by Super Admin
                      </span>
                    )}
                  </div>
                  <div className="relative">
                    <input
                      type="email"
                      value={adminEmail}
                      onChange={(e) => setAdminEmail(e.target.value)}
                      disabled={currentUser?.role !== 'super_admin'}
                      required
                      placeholder="admin@reuseit.shop"
                      className={`w-full pl-3.5 pr-28 py-2.5 rounded-xl text-xs border transition-colors outline-none
                        ${currentUser?.role === 'super_admin'
                          ? 'bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white focus:border-brand-500 light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]'
                          : 'bg-[#10231d]/60 border-[#22493c] text-[#90cbb7] cursor-not-allowed light:bg-gray-100 light:border-[#D5E4DC] light:text-[#527063]'
                        }`}
                    />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                      Verified SSO
                    </span>
                  </div>
                  {currentUser?.role === 'super_admin' && (
                    <p className="mt-1 text-[10px] text-[#90cbb7]/70">
                      Super Admin Jurisdiction: Saving changes updates the email required to sign in across the portal.
                    </p>
                  )}
                </div>

                {/* Bio / Staff Note */}
                <div>
                  <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                    Internal Staff Note (Audit Logs)
                  </label>
                  <textarea
                    rows={2}
                    value={bioNote}
                    onChange={(e) => setBioNote(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl text-xs outline-none border transition-colors resize-none
                      bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                      light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
                      focus:border-brand-500"
                  />
                </div>

                <div className="flex items-center justify-end gap-3 pt-2">
                  <button
                    type="submit"
                    className="px-6 py-2.5 rounded-xl bg-brand-500 hover:bg-brand-400 text-forest-void font-bold text-xs shadow-glow-sm transition-all flex items-center gap-2"
                  >
                    <Check className="w-4 h-4 stroke-[3]" />
                    <span>Save Profile Identity</span>
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Right Column: Live Support Desk Signature Preview */}
          <div className="space-y-6">
            <div className="rounded-3xl p-6 bg-[#152a23] dark:bg-[#152a23] border border-[#22493c] light:bg-white light:border-[#D5E4DC] shadow-card space-y-4">
              <div className="flex items-center gap-2 text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] uppercase tracking-wider">
                <Sparkles className="w-4 h-4 text-brand-400" />
                <span>Live Support Desk Signature Preview</span>
              </div>
              <p className="text-xs text-[#90cbb7]/80 dark:text-[#90cbb7]/80 light:text-[#527063]">
                When responding to dispute tickets or user inquiries, your official badge and profile look like this:
              </p>

              {/* Mock Support Ticket Message Card */}
              <div className="p-4 rounded-2xl bg-[#10231d] dark:bg-[#10231d] border border-[#22493c] light:bg-[#F4F8F5] light:border-[#D5E4DC] space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <img 
                      src={avatarUrl} 
                      alt={displayName}
                      className="w-8 h-8 rounded-full object-cover border border-brand-500" 
                    />
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="font-bold text-xs text-white dark:text-white light:text-[#0D1C17]">
                          {displayName}
                        </span>
                        <span className="text-[9px] font-bold px-1.5 py-0.2 rounded bg-brand-500/20 text-brand-400 border border-brand-500/30">
                          Super Admin
                        </span>
                      </div>
                      <span className="text-[10px] text-[#90cbb7]/60 dark:text-[#90cbb7]/60 light:text-[#527063]">
                        ReUseIt Staff Support • Just now
                      </span>
                    </div>
                  </div>
                  <MessageSquare className="w-4 h-4 text-brand-400" />
                </div>

                <div className="p-3 rounded-xl bg-[#152a23] dark:bg-[#152a23] border border-[#22493c] light:bg-white light:border-[#D5E4DC] text-xs text-white dark:text-white light:text-[#0D1C17] leading-relaxed">
                  "Hello! This is <span className="font-bold text-brand-400">{displayName}</span> from the ReUseIt Trust & Safety team. I have reviewed your escrow transaction inspection timeline and release conditions..."
                </div>
              </div>

              {/* Safety notice */}
              <div className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-300 text-xs flex items-start gap-2.5">
                <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5 text-blue-400" />
                <span className="leading-relaxed text-[11px]">
                  Staff names and avatars are cryptographically signed to audit trails to prevent identity spoofing across support desks.
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 2: TWO-FACTOR AUTHENTICATION (2FA) */}
      {/* ========================================================================= */}
      {activeTab === '2fa' && (
        <div className="space-y-6">
          {/* Method Selector Banner */}
          <div className="rounded-3xl p-6 bg-[#152a23] dark:bg-[#152a23] border border-[#22493c] light:bg-white light:border-[#D5E4DC] shadow-card flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <h3 className="text-base font-bold text-white dark:text-white light:text-[#0D1C17] flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-brand-400" />
                Multi-Factor Authentication Mode
              </h3>
              <p className="text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mt-1">
                Choose between Hardware/App TOTP Authenticator (maximum defense) or Email OTP verification.
              </p>
            </div>

            <div className="flex items-center gap-2 p-1.5 rounded-2xl bg-[#10231d] dark:bg-[#10231d] border border-[#22493c] light:bg-[#F4F8F5] light:border-[#D5E4DC]">
              <button
                type="button"
                onClick={() => setAuthMethod('totp')}
                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2
                  ${authMethod === 'totp'
                    ? 'bg-brand-500 text-forest-void shadow-glow-sm'
                    : 'text-[#90cbb7] hover:text-white light:text-[#527063] light:hover:text-[#0D1C17]'
                  }
                `}
              >
                <Smartphone className="w-3.5 h-3.5" />
                <span>Authenticator App (TOTP)</span>
              </button>

              <button
                type="button"
                onClick={handleSwitchToEmailOtp}
                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2
                  ${authMethod === 'email_otp'
                    ? 'bg-brand-500 text-forest-void shadow-glow-sm'
                    : 'text-[#90cbb7] hover:text-white light:text-[#527063] light:hover:text-[#0D1C17]'
                  }
                `}
              >
                <Mail className="w-3.5 h-3.5" />
                <span>Email OTP Fallback</span>
              </button>
            </div>
          </div>

          {twoFactorSuccess && (
            <div className="p-4 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 shrink-0" />
              <span className="text-xs font-semibold">{twoFactorSuccess}</span>
            </div>
          )}

          {twoFactorError && (
            <div className="p-4 rounded-2xl bg-rose-500/15 border border-rose-500/30 text-rose-300 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 shrink-0" />
              <span className="text-xs font-semibold">{twoFactorError}</span>
            </div>
          )}

          {/* If TOTP Selected */}
          {authMethod === 'totp' ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left 2 Columns: App Selector, QR Code & Setup */}
              <div className="lg:col-span-2 space-y-6">
                {/* 1. App Selector */}
                <div className="rounded-3xl p-6 bg-[#152a23] dark:bg-[#152a23] border border-[#22493c] light:bg-white light:border-[#D5E4DC] shadow-card space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-[#90cbb7] light:text-[#527063]">
                      Step 1: Select Your Preferred Authenticator App
                    </h4>
                    <span className="text-[11px] font-semibold text-brand-400">
                      RFC 6238 TOTP Standard
                    </span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                    {apps.map((app) => (
                      <button
                        key={app.id}
                        type="button"
                        onClick={() => setSelectedApp(app.id)}
                        className={`p-3 rounded-2xl border text-left transition-all relative
                          ${selectedApp === app.id
                            ? 'bg-brand-500/15 border-brand-500 ring-1 ring-brand-500 text-white'
                            : 'bg-[#10231d] dark:bg-[#10231d] border-[#22493c] text-[#90cbb7] hover:border-brand-500/40 light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]'
                          }
                        `}
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="w-7 h-7 rounded-xl bg-brand-500/20 text-brand-400 flex items-center justify-center font-bold text-xs border border-brand-500/30">
                            {app.iconLabel}
                          </span>
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#152a23] text-[#90cbb7] border border-[#22493c]">
                            {app.badge}
                          </span>
                        </div>
                        <p className="text-xs font-bold truncate text-white dark:text-white light:text-[#0D1C17]">
                          {app.name}
                        </p>
                      </button>
                    ))}
                  </div>

                  {/* App instruction callout */}
                  <div className="p-3.5 rounded-2xl bg-[#10231d] dark:bg-[#10231d] border border-[#22493c] light:bg-[#F4F8F5] light:border-[#D5E4DC] flex items-center gap-3">
                    <div className="w-8 h-8 rounded-xl bg-brand-500/20 text-brand-400 flex items-center justify-center shrink-0">
                      <ExternalLink className="w-4 h-4" />
                    </div>
                    <p className="text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                      <span className="font-bold text-white dark:text-white light:text-[#0D1C17]">{currentAppObj.name}:</span> {currentAppObj.note}
                    </p>
                  </div>
                </div>

                {/* 2. QR Code and Secret Key */}
                <div className="rounded-3xl p-6 bg-[#152a23] dark:bg-[#152a23] border border-[#22493c] light:bg-white light:border-[#D5E4DC] shadow-card space-y-6">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-[#90cbb7] light:text-[#527063]">
                    Step 2: Scan QR Code or Input Secret Key
                  </h4>

                  <div className="flex flex-col sm:flex-row items-center gap-6">
                    {/* Real Scannable Authenticator QR Code */}
                    <div className="p-4 rounded-3xl bg-white border border-[#22493c] shadow-glow-sm shrink-0 flex flex-col items-center">
                      <div className="p-2 bg-white rounded-2xl flex items-center justify-center">
                        <QRCodeSVG
                          value={totpData.uri}
                          size={144}
                          level="M"
                          includeMargin={false}
                          bgColor="#FFFFFF"
                          fgColor="#08130f"
                        />
                      </div>
                      <span className="text-[10px] font-bold text-gray-700 mt-2">
                        Scan with {currentAppObj.name}
                      </span>
                    </div>

                    {/* Manual Secret Key */}
                    <div className="flex-1 w-full space-y-3">
                      <p className="text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                        If you cannot scan the code with your device camera, enter this 24-character security key manually into your app:
                      </p>

                      <div className="p-3.5 rounded-2xl bg-[#10231d] dark:bg-[#10231d] border border-[#22493c] light:bg-[#F4F8F5] light:border-[#D5E4DC] flex items-center justify-between gap-3">
                        <code className="text-xs font-mono font-bold text-brand-400 tracking-wider truncate">
                          {totpData.formattedSecret}
                        </code>
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            type="button"
                            onClick={() => setTotpData(generateTotpSecret(userAdminEmail))}
                            title="Regenerate QR code"
                            className="p-1.5 rounded-xl bg-[#152a23] text-slate-400 hover:text-white border border-[#22493c] transition-colors"
                          >
                            <RefreshCw className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={handleCopySecret}
                            className="px-3 py-1.5 rounded-xl bg-brand-500/20 text-brand-400 border border-brand-500/30 hover:bg-brand-500 hover:text-forest-void text-xs font-bold transition-all flex items-center gap-1.5"
                          >
                            {secretCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                            <span>{secretCopied ? 'Copied' : 'Copy'}</span>
                          </button>
                        </div>
                      </div>

                      <div className="text-[11px] text-[#90cbb7]/70 dark:text-[#90cbb7]/70 light:text-[#527063] space-y-1">
                        <p>• Account Name: <strong className="text-white dark:text-white light:text-[#0D1C17]">{currentUser?.email || 'admin@reuseit.shop'}</strong></p>
                        <p>• Issuer: <strong className="text-white dark:text-white light:text-[#0D1C17]">ReUseIt Super Admin</strong></p>
                        <p>• Type: <strong className="text-white dark:text-white light:text-[#0D1C17]">Time-based (30s interval)</strong></p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 3. 6-Digit Code Verification */}
                <div className="rounded-3xl p-6 bg-[#152a23] dark:bg-[#152a23] border border-[#22493c] light:bg-white light:border-[#D5E4DC] shadow-card space-y-4">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-[#90cbb7] light:text-[#527063]">
                    Step 3: Enter 6-Digit Verification Code from {currentAppObj.name}
                  </h4>

                  <form onSubmit={handleVerifyTotp} className="flex flex-col sm:flex-row items-center gap-3">
                    <input
                      type="text"
                      maxLength={6}
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="e.g. 123456"
                      className="w-full sm:w-64 px-4 py-3 rounded-2xl text-center text-lg font-mono font-bold tracking-[0.3em] outline-none border transition-colors
                        bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-brand-400
                        light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
                        focus:border-brand-500"
                    />

                    <button
                      type="submit"
                      className="w-full sm:w-auto px-6 py-3 rounded-2xl bg-brand-500 hover:bg-brand-400 text-forest-void font-extrabold text-xs shadow-glow-sm transition-all flex items-center justify-center gap-2 shrink-0"
                    >
                      <ShieldCheck className="w-4 h-4 stroke-[3]" />
                      <span>Verify & Confirm 2FA</span>
                    </button>
                  </form>
                </div>
              </div>

              {/* Right Column: Emergency Backup Codes & Revert Option */}
              <div className="space-y-6">
                {/* Emergency Backup Codes Card */}
                <div className="rounded-3xl p-6 bg-[#152a23] dark:bg-[#152a23] border border-[#22493c] light:bg-white light:border-[#D5E4DC] shadow-card space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-[#90cbb7] light:text-[#527063] flex items-center gap-2">
                      <Lock className="w-4 h-4 text-brand-400" />
                      <span>One-Time Backup Codes</span>
                    </h4>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-brand-500/20 text-brand-400">
                      5 Codes Left
                    </span>
                  </div>

                  <p className="text-xs text-[#90cbb7]/80 dark:text-[#90cbb7]/80 light:text-[#527063]">
                    If you lose access to your phone or authenticator app, these emergency codes can be used once each to log in.
                  </p>

                  <div className="p-3.5 rounded-2xl bg-[#10231d] dark:bg-[#10231d] border border-[#22493c] light:bg-[#F4F8F5] light:border-[#D5E4DC] space-y-2">
                    {backupCodes.map((code, idx) => (
                      <div key={code} className="flex items-center justify-between text-xs font-mono py-1 px-2 rounded-lg hover:bg-[#152a23]">
                        <span className="text-[#90cbb7]/60">#{idx + 1}</span>
                        <span className="font-bold text-white dark:text-white light:text-[#0D1C17]">{code}</span>
                        <span className="text-[10px] text-emerald-400 font-medium">Valid</span>
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={handleCopyBackupCodes}
                    className="w-full py-2.5 rounded-xl border border-brand-500/30 bg-brand-500/10 hover:bg-brand-500/20 text-brand-400 text-xs font-bold transition-all flex items-center justify-center gap-2"
                  >
                    {backupCodesCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    <span>{backupCodesCopied ? 'All Codes Copied' : 'Copy All Backup Codes'}</span>
                  </button>
                </div>

                {/* Switch to Email OTP Fallback */}
                <div className="rounded-3xl p-6 bg-[#152a23] dark:bg-[#152a23] border border-[#22493c] light:bg-white light:border-[#D5E4DC] shadow-card space-y-3">
                  <h4 className="text-xs font-bold text-white dark:text-white light:text-[#0D1C17] flex items-center gap-2">
                    <Mail className="w-4 h-4 text-amber-400" />
                    Prefer Email OTP Instead?
                  </h4>
                  <p className="text-xs text-[#90cbb7]/80 dark:text-[#90cbb7]/80 light:text-[#527063] leading-relaxed">
                    You can disable the authenticator app requirement and receive a 6-digit one-time passcode at <strong className="text-white dark:text-white light:text-[#0D1C17]">{currentUser?.email || 'admin@reuseit.shop'}</strong> each time you sign in.
                  </p>

                  <button
                    type="button"
                    onClick={handleSwitchToEmailOtp}
                    className="w-full py-2.5 rounded-xl bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/30 text-amber-300 text-xs font-bold transition-all flex items-center justify-center gap-2"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    <span>Disable App & Use Email OTP</span>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* If Email OTP Selected */
            <div className="rounded-3xl p-8 bg-[#152a23] dark:bg-[#152a23] border border-[#22493c] light:bg-white light:border-[#D5E4DC] shadow-card max-w-2xl mx-auto space-y-6 text-center">
              <div className="w-16 h-16 rounded-3xl bg-brand-500/20 text-brand-400 border border-brand-500/30 flex items-center justify-center mx-auto">
                <Mail className="w-8 h-8" />
              </div>

              <div>
                <h3 className="text-xl font-bold text-white dark:text-white light:text-[#0D1C17]">
                  Email OTP Verification Active
                </h3>
                <p className="text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mt-2 max-w-md mx-auto">
                  Every time you sign in to the ReUseIt Super Admin Portal, a secure 6-digit single-use passcode is automatically dispatched to <strong className="text-white dark:text-white light:text-[#0D1C17]">{currentUser?.email || 'admin@reuseit.shop'}</strong>.
                </p>
              </div>

              <div className="p-4 rounded-2xl bg-[#10231d] dark:bg-[#10231d] border border-[#22493c] light:bg-[#F4F8F5] light:border-[#D5E4DC] text-left text-xs space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[#90cbb7]">Registered Email:</span>
                  <span className="font-bold text-white dark:text-white light:text-[#0D1C17]">{currentUser?.email || 'admin@reuseit.shop'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[#90cbb7]">Code Expiry:</span>
                  <span className="font-bold text-white dark:text-white light:text-[#0D1C17]">10 Minutes</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[#90cbb7]">Rate Limit:</span>
                  <span className="font-bold text-white dark:text-white light:text-[#0D1C17]">3 attempts per 5 minutes</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={() => setAuthMethod('totp')}
                  className="w-full sm:w-auto px-6 py-3 rounded-2xl bg-brand-500 hover:bg-brand-400 text-forest-void font-extrabold text-xs shadow-glow-sm transition-all flex items-center justify-center gap-2"
                >
                  <Smartphone className="w-4 h-4" />
                  <span>Upgrade to Authenticator App (Recommended)</span>
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 3: PASSWORD & SECURITY */}
      {/* ========================================================================= */}
      {activeTab === 'password' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left 2 Columns: Password Update Form */}
          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-3xl p-6 sm:p-8 bg-[#152a23] dark:bg-[#152a23] border border-[#22493c] light:bg-white light:border-[#D5E4DC] shadow-card space-y-6">
              <div>
                <h3 className="text-lg font-bold text-white dark:text-white light:text-[#0D1C17] flex items-center gap-2">
                  <KeyRound className="w-5 h-5 text-amber-400" />
                  Update Admin Password
                </h3>
                <p className="text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mt-1">
                  Ensure your Super Admin password is strong, unique, and not shared with other accounts.
                </p>
              </div>

              {passwordSuccess && (
                <div className="p-4 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 shrink-0" />
                  <span className="text-xs font-semibold">{passwordSuccess}</span>
                </div>
              )}

              {passwordError && (
                <div className="p-4 rounded-2xl bg-rose-500/15 border border-rose-500/30 text-rose-300 flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 shrink-0" />
                  <span className="text-xs font-semibold">{passwordError}</span>
                </div>
              )}

              <form onSubmit={handleUpdatePassword} className="space-y-5">
                {/* Current Password */}
                <div>
                  <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                    Current Password
                  </label>
                  <div className="relative">
                    <input
                      type={showCurrentPw ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="••••••••••••"
                      required
                      className="w-full pl-3.5 pr-10 py-2.5 rounded-xl text-xs outline-none border transition-colors
                        bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                        light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
                        focus:border-brand-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPw(!showCurrentPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#90cbb7] hover:text-white"
                    >
                      {showCurrentPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div>
                  <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                    New Password (min 8 characters)
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPw ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="••••••••••••"
                      required
                      className="w-full pl-3.5 pr-10 py-2.5 rounded-xl text-xs outline-none border transition-colors
                        bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                        light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
                        focus:border-brand-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPw(!showNewPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#90cbb7] hover:text-white"
                    >
                      {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>

                  {/* Password Strength Meter */}
                  {newPassword && (
                    <div className="mt-3 space-y-2">
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-[#90cbb7] light:text-[#527063]">Password Strength:</span>
                        <span className={`font-bold ${
                          strengthScore <= 2 ? 'text-rose-400' : strengthScore <= 4 ? 'text-amber-400' : 'text-emerald-400'
                        }`}>
                          {strengthScore <= 2 ? 'Weak' : strengthScore <= 4 ? 'Moderate' : 'Ultra-Secure'}
                        </span>
                      </div>
                      <div className="w-full bg-[#10231d] rounded-full h-1.5 overflow-hidden">
                        <div 
                          className={`h-full transition-all duration-300 ${
                            strengthScore <= 2 ? 'bg-rose-500 w-1/3' : strengthScore <= 4 ? 'bg-amber-500 w-2/3' : 'bg-emerald-500 w-full'
                          }`}
                        />
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 text-[10px]">
                        <span className={newPassword.length >= 8 ? 'text-emerald-400 font-semibold' : 'text-[#90cbb7]/60'}>
                          ✓ 8+ Characters
                        </span>
                        <span className={/[A-Z]/.test(newPassword) ? 'text-emerald-400 font-semibold' : 'text-[#90cbb7]/60'}>
                          ✓ Uppercase letter
                        </span>
                        <span className={/[0-9]/.test(newPassword) ? 'text-emerald-400 font-semibold' : 'text-[#90cbb7]/60'}>
                          ✓ Number
                        </span>
                        <span className={/[^A-Za-z0-9]/.test(newPassword) ? 'text-emerald-400 font-semibold' : 'text-[#90cbb7]/60'}>
                          ✓ Special symbol
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••••••"
                    required
                    className="w-full px-3.5 py-2.5 rounded-xl text-xs outline-none border transition-colors
                      bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                      light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
                      focus:border-brand-500"
                  />
                </div>

                <div className="flex items-center justify-end gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={passwordLoading}
                    className="px-6 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-forest-void font-extrabold text-xs shadow-glow-sm transition-all flex items-center gap-2"
                  >
                    <KeyRound className="w-4 h-4" />
                    <span>{passwordLoading ? 'Encrypting & Updating...' : 'Update Password'}</span>
                  </button>
                </div>
              </form>
            </div>

            {/* Active Sessions & Logins */}
            <div className="rounded-3xl p-6 bg-[#152a23] dark:bg-[#152a23] border border-[#22493c] light:bg-white light:border-[#D5E4DC] shadow-card space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-bold uppercase tracking-wider text-[#90cbb7] light:text-[#527063] flex items-center gap-2">
                  <Laptop className="w-4 h-4 text-brand-400" />
                  <span>Active Super Admin Sessions</span>
                </h4>
                <button
                  type="button"
                  onClick={() => alert('All other remote browser sessions terminated.')}
                  className="text-[11px] font-bold text-rose-400 hover:underline"
                >
                  Revoke Other Sessions
                </button>
              </div>

              <div className="space-y-2.5">
                <div className="p-3 rounded-2xl bg-[#10231d] border border-brand-500/40 flex items-center justify-between text-xs">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-xl bg-brand-500/20 text-brand-400 flex items-center justify-center">
                      <Laptop className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="font-bold text-white">Chrome on Windows (Current)</p>
                      <p className="text-[10px] text-[#90cbb7]">IP: 197.210.44.18 • Lagos, Nigeria</p>
                    </div>
                  </div>
                  <span className="px-2 py-0.5 rounded-md text-[10px] font-extrabold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                    This Device
                  </span>
                </div>

                <div className="p-3 rounded-2xl bg-[#10231d] border border-[#22493c] flex items-center justify-between text-xs opacity-75">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-xl bg-[#152a23] text-[#90cbb7] flex items-center justify-center">
                      <Globe className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="font-bold text-white">Safari on iOS</p>
                      <p className="text-[10px] text-[#90cbb7]">IP: 102.89.33.12 • Last active 2 days ago</p>
                    </div>
                  </div>
                  <span className="text-[10px] text-[#90cbb7]/60">Expired</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Forgot Password via Email OTP & Firebase Reset */}
          <div className="space-y-6">
            <div className="rounded-3xl p-6 bg-[#152a23] dark:bg-[#152a23] border border-[#22493c] light:bg-white light:border-[#D5E4DC] shadow-card space-y-4">
              <div className="flex items-center gap-2 text-xs font-bold text-amber-400 uppercase tracking-wider">
                <Mail className="w-4 h-4" />
                <span>Forgot Current Password?</span>
              </div>

              {!forgotPasswordMode ? (
                <div className="space-y-4">
                  <p className="text-xs text-[#90cbb7]/80 dark:text-[#90cbb7]/80 light:text-[#527063] leading-relaxed">
                    If you cannot recall your credentials, you can initiate a self-service password reset. A secure 6-digit OTP passcode will be dispatched directly to your registered administrator email.
                  </p>

                  <div className="p-3.5 rounded-2xl bg-[#10231d] dark:bg-[#10231d] border border-[#22493c] light:bg-[#F4F8F5] light:border-[#D5E4DC] text-xs">
                    <span className="text-[#90cbb7]/60 block text-[10px] uppercase font-semibold">Recipient:</span>
                    <span className="font-bold text-white dark:text-white light:text-[#0D1C17]">{currentUser?.email || 'admin@reuseit.shop'}</span>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setForgotPasswordMode(true);
                      handleSendForgotOtp();
                    }}
                    disabled={forgotLoading}
                    className="w-full py-3 rounded-xl bg-amber-500 hover:bg-amber-400 text-forest-void font-extrabold text-xs shadow-glow-sm transition-all flex items-center justify-center gap-2"
                  >
                    <Mail className="w-4 h-4" />
                    <span>{forgotLoading ? 'Dispatching OTP...' : 'Initiate Password Reset (Email OTP)'}</span>
                  </button>

                  <div className="relative flex py-1 items-center">
                    <div className="flex-grow border-t border-[#22493c] light:border-[#D5E4DC]" />
                    <span className="flex-shrink mx-3 text-[10px] text-[#90cbb7]/60 uppercase font-bold">or via link</span>
                    <div className="flex-grow border-t border-[#22493c] light:border-[#D5E4DC]" />
                  </div>

                  <button
                    type="button"
                    onClick={handleSendResetEmail}
                    disabled={passwordLoading}
                    className="w-full py-2.5 rounded-xl border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 text-xs font-bold transition-all flex items-center justify-center gap-2"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    <span>{emailResetSent ? 'Reset Link Dispatched!' : 'Send Firebase Email Link'}</span>
                  </button>
                </div>
              ) : (
                /* Interactive Forgot Password with 6-digit OTP verification */
                <form onSubmit={handleVerifyOtpAndResetPassword} className="space-y-4">
                  <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs space-y-1">
                    <p className="font-bold">6-Digit OTP Dispatched!</p>
                    <p className="text-[11px] text-[#90cbb7]">
                      Check <span className="text-white font-medium">{currentUser?.email || 'admin@reuseit.shop'}</span> for your verification code.
                    </p>
                    {generatedForgotOtp && (
                      <p className="text-[10px] text-brand-300 font-mono pt-1">
                        (Demo Code: <strong className="text-white bg-brand-500/20 px-1.5 py-0.5 rounded">{generatedForgotOtp}</strong> or 123456)
                      </p>
                    )}
                  </div>

                  {forgotError && (
                    <div className="p-3 rounded-xl bg-rose-500/15 border border-rose-500/30 text-rose-300 text-xs">
                      {forgotError}
                    </div>
                  )}

                  <div>
                    <label className="block text-[11px] font-bold text-[#90cbb7] light:text-[#527063] mb-1">
                      Enter 6-Digit Email OTP:
                    </label>
                    <input
                      type="text"
                      maxLength={6}
                      value={enteredForgotOtp}
                      onChange={(e) => setEnteredForgotOtp(e.target.value.replace(/\D/g, ''))}
                      placeholder="e.g. 492019"
                      required
                      className="w-full px-3.5 py-2.5 rounded-xl text-center font-mono text-sm tracking-widest outline-none border transition-colors
                        bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                        light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
                        focus:border-amber-400"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-[#90cbb7] light:text-[#527063] mb-1">
                      New Password:
                    </label>
                    <div className="relative">
                      <input
                        type={showForgotNewPw ? 'text' : 'password'}
                        value={forgotNewPassword}
                        onChange={(e) => setForgotNewPassword(e.target.value)}
                        placeholder="••••••••••••"
                        required
                        className="w-full pl-3.5 pr-9 py-2 rounded-xl text-xs outline-none border transition-colors
                          bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                          light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
                          focus:border-brand-500"
                      />
                      <button
                        type="button"
                        onClick={() => setShowForgotNewPw(!showForgotNewPw)}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#90cbb7]"
                      >
                        {showForgotNewPw ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-[#90cbb7] light:text-[#527063] mb-1">
                      Confirm New Password:
                    </label>
                    <input
                      type="password"
                      value={forgotConfirmPassword}
                      onChange={(e) => setForgotConfirmPassword(e.target.value)}
                      placeholder="••••••••••••"
                      required
                      className="w-full px-3.5 py-2 rounded-xl text-xs outline-none border transition-colors
                        bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                        light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
                        focus:border-brand-500"
                    />
                  </div>

                  <div className="flex items-center gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => setForgotPasswordMode(false)}
                      className="flex-1 py-2.5 rounded-xl border border-[#22493c] text-xs font-semibold text-[#90cbb7] hover:text-white"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={forgotLoading}
                      className="flex-1 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-forest-void font-extrabold text-xs shadow-glow-sm transition-all"
                    >
                      {forgotLoading ? 'Verifying...' : 'Reset Password'}
                    </button>
                  </div>
                </form>
              )}

              <p className="text-[10px] text-[#90cbb7]/60 text-center">
                All password reset events are cryptographically audited in Super Admin security logs.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Avatar Cropper Modal (1:1 Aspect Ratio) */}
      {avatarCropperOpen && (
        <ImageCropperModal
          isOpen={avatarCropperOpen}
          onClose={() => setAvatarCropperOpen(false)}
          imageSrc={rawAvatarImageSrc}
          aspectRatio="1:1"
          title="Crop & Optimize Staff Profile Avatar (1:1)"
          onCropComplete={handleAvatarCropComplete}
        />
      )}
    </div>
  );
};
